import { CSPaginationRules } from "types";

type buildPaginationRulesProps = {
  total?: number;
  page?: number;
  size?: number;
  pageTotal?: number;
  pageMax?: number;
};

const buildPaginationRules = ({
  total = 0,
  page = 0,
  size = 0,
  pageTotal = 0,
  pageMax = 5,
}: buildPaginationRulesProps): CSPaginationRules => {
  //NOTE: Some of the logic below was ripped from:
  // https://jasonwatmore.com/post/2018/08/07/javascript-pure-pagination-logic-in-vanilla-js-typescript

  let currentPage = page;
  // ensure current page isn't out of range
  if (page < 1) {
    currentPage = 1;
  } else if (currentPage > pageTotal) {
    currentPage = pageTotal;
  }

  let startPage = 0;
  let endPage = 0;
  if (pageTotal <= pageMax) {
    // total pages less than max so show all pages
    startPage = 1;
    endPage = pageTotal;
  } else {
    // total pages more than max so calculate start and end pages
    const maxPagesBeforeCurrentPage = Math.floor(pageMax / 2);
    const maxPagesAfterCurrentPage = Math.ceil(pageMax / 2) - 1;
    if (currentPage <= maxPagesBeforeCurrentPage) {
      // current page near the start
      startPage = 1;
      endPage = pageMax;
    } else if (currentPage + maxPagesAfterCurrentPage >= pageTotal) {
      // current page near the end
      startPage = pageTotal - pageMax + 1;
      endPage = pageTotal;
    } else {
      // current page somewhere in the middle
      startPage = currentPage - maxPagesBeforeCurrentPage;
      endPage = currentPage + maxPagesAfterCurrentPage;
    }
  }

  // calculate start and end item indexes
  const startIndex = (currentPage - 1) * size;
  const endIndex = Math.min(startIndex + size - 1, total - 1);

  // create an array of pages to repeat in the pager control
  const pageNumbers = Array.from(Array(endPage + 1 - startPage).keys()).map((i) => startPage + i);

  const hasPages = pageNumbers.length > 1;

  return {
    startIndex,
    endIndex,
    startPage,
    endPage,
    hasFirst: total > 0 && currentPage > 1,
    hasPrev: currentPage > 1,
    hasNext: currentPage < pageTotal,
    hasLast: total > size && currentPage < pageTotal,
    outOfRange: hasPages && endPage < page,
    hasPages,
    pageNumbers,
  };
};

export default buildPaginationRules;
